import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, Eyebrow, EmptyState, Spinner, formatDate, formatDuration, formatTime, money } from '@busbook/ui';
import type { TripSearchResult } from '@busbook/shared';
import { useTenant } from '../lib/tenant.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

/**
 * Results are a departure board: the time is the largest thing on the row,
 * because it is what a passenger actually scans for. The rail between origin and
 * destination encodes the journey itself, not decoration.
 */
export function Results() {
  const t = useT();
  const { settings } = useTenant();
  const [params] = useSearchParams();
  const [trips, setTrips] = useState<TripSearchResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const date = params.get('date') ?? '';
  const passengers = params.get('passengers') ?? '1';

  useEffect(() => {
    let cancelled = false;
    setTrips(null);
    setError(null);

    void (async () => {
      try {
        const query = new URLSearchParams({ from, to, date, passengers });
        const result = await api.request<TripSearchResult[]>(
          `/public/trips?${query.toString()}`,
          { method: 'GET', auth: false },
        );
        if (!cancelled) setTrips(result);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : 'We could not load departures. Check your connection.',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [from, to, date, passengers]);

  if (error) {
    return (
      <section className="px-4 py-8">
        <EmptyState title={t('r.unavailable')} description={error} />
      </section>
    );
  }

  if (!trips) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner label={t('r.finding')} />
      </div>
    );
  }

  if (trips.length === 0) {
    return (
      <section className="px-4 py-8">
        <EmptyState
          title={t('r.none')}
          description={`Nothing runs on ${formatDate(`${date}T12:00:00Z`, settings.timezone)} for that journey, or seats have sold out.`}
          action={
            <Link
              to="/"
              className="text-sm font-medium underline underline-offset-4"
              style={{ color: 'var(--color-primary)' }}
            >
              {t('r.changeSearch')}
            </Link>
          }
        />
      </section>
    );
  }

  return (
    <section className="px-4 py-6">
      <Eyebrow>{formatDate(`${date}T12:00:00Z`, settings.timezone)}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">
        {trips[0]!.fromStopName} → {trips[0]!.toStopName}
      </h1>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        {trips.length} departure{trips.length === 1 ? '' : 's'} · {passengers} passenger
        {passengers === '1' ? '' : 's'}
      </p>

      <ul className="mt-5 flex flex-col gap-3">
        {trips.map((trip) => (
          <li key={trip.id}>
            <Link
              to={`/trip/${trip.id}/seats?from=${from}&to=${to}&date=${date}&passengers=${passengers}`}
              className="block rounded-xl border p-4 transition-colors"
              style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
            >
              <div className="flex items-center gap-3">
                <time className="board-time" dateTime={trip.departureAt}>
                  {formatTime(trip.departureAt, settings.timezone)}
                </time>
                <span className="rail" aria-hidden />
                <time className="board-time" dateTime={trip.arrivalAt}>
                  {formatTime(trip.arrivalAt, settings.timezone)}
                </time>
              </div>

              <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  {formatDuration(trip.durationMinutes)} · {trip.busName}
                  {trip.delayMinutes > 0 && (
                    <span style={{ color: 'var(--signal-delayed)' }}>
                      {' '}· delayed {trip.delayMinutes} min
                    </span>
                  )}
                </p>
                <p className="money text-base font-semibold">
                  {money(trip.priceCents, trip.currency)}
                </p>
              </div>

              <p className="mt-1 text-xs" style={{ color: trip.seatsLeft <= 5 ? 'var(--signal-delayed)' : 'var(--color-faint)' }}>
                {trip.seatsLeft} seat{trip.seatsLeft === 1 ? '' : 's'} left
              </p>

              {trip.note && (
                <p className="mt-2 text-xs font-medium" style={{ color: 'var(--signal-delayed)' }}>
                  {trip.note}
                </p>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
